/**
 * CDC Fit Connector
 * -----------------
 * Shared engine for the "Fit Finder on PDP" A/B variants (V1 hero / V2 compact / V3 direct).
 *
 * Responsibilities:
 *  - Compute a size from band + cup using the section's size chart (ported from cdc-fit-hero).
 *  - On "See my match", drive the theme's real <variant-picker> to the matching Colour + Size
 *    using the theme's own supported selection path (custom-select.selectOption for dropdowns,
 *    checked + bubbling change for radios). This keeps price, availability, gallery filter and
 *    the ATC button in sync for free, and stays on the current product page.
 *  - Present the choice as a modal, a bottom sheet, or a direct select + toast, per data-mode.
 *
 * Contract (set on a [data-cdc-fit] root element):
 *   data-cdc-fit                       marker attribute (required)
 *   data-cdc-fit-mode                  "modal" | "sheet" | "direct"   (default "modal")
 *   data-cdc-fit-picker                CSS selector for the target variant-picker (default "variant-picker")
 *   data-cdc-fit-size-option           product option name for size   (default "Size")
 *   data-cdc-fit-color-option          product option name for colour (default "Color")
 *   data-cdc-fit-color-pick            "true" | "false" let shopper pick colour in popup (default "true")
 *   data-cdc-fit-scroll                "true" | "false" scroll to picker after applying (default "true")
 *   data-cdc-fit-scroll-target         CSS selector to scroll to (default = the picker)
 *
 * Child hooks inside the root:
 *   [data-cdc-fit-band]                <select> band
 *   [data-cdc-fit-cup]                 <select> cup
 *   [data-cdc-fit-size]                element whose textContent shows the live computed size
 *   [data-cdc-fit-cta]                 the "See my match" button
 *   script[type="application/json"][data-cdc-fit-config]   { chart, overrides, copy }
 */
(function () {
  'use strict';

  var CANONICAL_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

  /* ------------------------------------------------------------------ *
   * Size chart parsing + compute (ported verbatim in behaviour from
   * cdc-fit-hero so results are identical to the landing page).
   * ------------------------------------------------------------------ */
  function buildChart(chartRaw) {
    var CUP_ALIAS = { DDD: 'E', GG: 'G' };
    function normalizeCup(cup) {
      var up = String(cup).trim().toUpperCase();
      return CUP_ALIAS[up] || up;
    }

    var bands = [];
    var rows = {};
    String(chartRaw || '').split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line) return;
      var sep = line.indexOf(':');
      if (sep < 0) return;
      var key = line.slice(0, sep).trim();
      var rest = line.slice(sep + 1).trim();
      var values = rest.split(',').map(function (v) { return v.trim(); });
      if (/^bands$/i.test(key)) {
        bands = values;
      } else {
        rows[normalizeCup(key)] = values;
      }
    });

    var bandNums = bands.map(function (b) { return parseInt(b, 10); });
    var cupOrder = Object.keys(rows);

    function bandColumnIndex(band) {
      var num = parseInt(band, 10);
      var exact = bands.indexOf(String(band).trim());
      if (exact > -1) return exact;
      if (isNaN(num)) return 0;
      var bestIndex = 0;
      var bestDiff = Infinity;
      for (var i = 0; i < bandNums.length; i++) {
        if (isNaN(bandNums[i])) continue;
        if (bandNums[i] <= num) {
          var diff = num - bandNums[i];
          if (diff <= bestDiff) { bestDiff = diff; bestIndex = i; }
        }
      }
      if (bestDiff === Infinity) {
        var minIndex = 0, minVal = Infinity;
        for (var j = 0; j < bandNums.length; j++) {
          if (bandNums[j] < minVal) { minVal = bandNums[j]; minIndex = j; }
        }
        return minIndex;
      }
      return bestIndex;
    }

    function resolveCell(cupKey, colIndex) {
      var cupPos = cupOrder.indexOf(cupKey);
      if (cupPos < 0) cupPos = 0;
      var row = rows[cupOrder[cupPos]] || [];
      var value = (row[colIndex] || '').trim();
      if (value && value !== '/') return value;
      for (var down = cupPos - 1; down >= 0; down--) {
        var v = ((rows[cupOrder[down]] || [])[colIndex] || '').trim();
        if (v && v !== '/') return v;
      }
      for (var up = cupPos + 1; up < cupOrder.length; up++) {
        var v2 = ((rows[cupOrder[up]] || [])[colIndex] || '').trim();
        if (v2 && v2 !== '/') return v2;
      }
      return '';
    }

    return {
      normalizeCup: normalizeCup,
      cupOrder: cupOrder,
      rows: rows,
      compute: function (band, cup, overrides) {
        var key = (String(band).trim() + String(cup).trim()).toUpperCase();
        if (overrides && overrides[key]) return overrides[key];
        var cupKey = normalizeCup(cup);
        if (!rows[cupKey]) cupKey = cupOrder[0] || cupKey;
        var colIndex = bandColumnIndex(band);
        return resolveCell(cupKey, colIndex) || 'M';
      }
    };
  }

  function parseOverrides(raw) {
    var map = {};
    String(raw || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split(':');
      if (parts.length === 2) map[parts[0].trim().toUpperCase()] = parts[1].trim().toUpperCase();
    });
    return map;
  }

  /* ------------------------------------------------------------------ *
   * Variant picker helpers — mirror the theme's own applySearchParams.
   * ------------------------------------------------------------------ */
  function findSelector(picker, optionName) {
    if (!picker) return null;
    var wanted = String(optionName || '').trim().toLowerCase();
    var selectors = picker.querySelectorAll('.option-selector');
    for (var i = 0; i < selectors.length; i++) {
      var name = (selectors[i].getAttribute('data-option') || '').trim().toLowerCase();
      if (name === wanted) return selectors[i];
    }
    return null;
  }

  function isDropdown(selector) {
    return selector && selector.getAttribute('data-selector-type') === 'dropdown';
  }

  function optionValues(selector) {
    if (!selector) return [];
    var out = [];
    if (isDropdown(selector)) {
      selector.querySelectorAll('.custom-select__option').forEach(function (o) {
        var v = o.getAttribute('data-value');
        if (v !== '' && v != null) out.push(v);
      });
    } else {
      selector.querySelectorAll('input.js-option').forEach(function (i) {
        if (i.value !== '') out.push(i.value);
      });
    }
    return out;
  }

  function currentValue(selector) {
    if (!selector) return null;
    if (isDropdown(selector)) {
      var sel = selector.querySelector('.custom-select__option[aria-selected="true"]');
      return sel ? sel.getAttribute('data-value') : null;
    }
    var chk = selector.querySelector('input.js-option:checked');
    return chk ? chk.value : null;
  }

  function eq(a, b) {
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  }

  function setOption(selector, value) {
    if (!selector || value == null) return false;
    if (isDropdown(selector)) {
      var opts = selector.querySelectorAll('.custom-select__option');
      for (var i = 0; i < opts.length; i++) {
        if (eq(opts[i].getAttribute('data-value'), value)) {
          var cs = opts[i].closest('custom-select');
          if (cs && typeof cs.selectOption === 'function') { cs.selectOption(opts[i]); return true; }
          // Fallback if the custom element has not upgraded yet.
          opts[i].setAttribute('aria-selected', 'true');
          selector.dispatchEvent(new CustomEvent('change', { bubbles: true }));
          return true;
        }
      }
      return false;
    }
    var inputs = selector.querySelectorAll('input.js-option');
    for (var j = 0; j < inputs.length; j++) {
      if (eq(inputs[j].value, value)) {
        inputs[j].checked = true;
        inputs[j].dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  function normSize(s) {
    var up = String(s || '').replace(/\s+/g, '').toUpperCase();
    up = up.replace(/^XXXXX/, '5X').replace(/^XXXX/, '4X').replace(/^XXX/, '3X').replace(/^XX/, '2X');
    return up;
  }

  // Match a computed size to a real option value; if the exact size is not
  // offered, snap to the nearest available size by canonical order.
  function resolveSizeValue(sizeSelector, size) {
    var values = optionValues(sizeSelector);
    if (!values.length) return null;
    var target = normSize(size);
    for (var i = 0; i < values.length; i++) {
      if (normSize(values[i]) === target) return values[i];
    }
    var targetIdx = CANONICAL_SIZES.indexOf(target);
    if (targetIdx === -1) return null;
    var best = null;
    var bestDist = Infinity;
    values.forEach(function (v) {
      var idx = CANONICAL_SIZES.indexOf(normSize(v));
      if (idx === -1) return;
      var dist = Math.abs(idx - targetIdx);
      if (dist < bestDist) { bestDist = dist; best = v; }
    });
    return best;
  }

  // Read the swatch background (colour or image) the theme rendered for a colour value.
  function swatchStyleFor(picker, value) {
    var wanted = String(value).replace(/"/g, '').toLowerCase();
    var el = picker.querySelector('[data-swatch="' + (window.CSS && CSS.escape ? CSS.escape(wanted) : wanted) + '"]');
    if (!el) {
      var candidates = picker.querySelectorAll('[data-swatch]');
      for (var i = 0; i < candidates.length; i++) {
        if ((candidates[i].getAttribute('data-swatch') || '').toLowerCase() === wanted) { el = candidates[i]; break; }
      }
    }
    if (!el) return '';
    var inline = el.getAttribute('style') || '';
    var m = inline.match(/--swatch-background-color:\s*([^;]+)/i);
    if (m) return 'background-color:' + m[1].trim() + ';';
    var cs = window.getComputedStyle(el);
    var bg = cs.getPropertyValue('--swatch-background-color') || cs.backgroundColor;
    var img = cs.getPropertyValue('--swatch-background-image');
    if (img && img !== 'none') return 'background-image:' + img.trim() + ';background-size:cover;';
    if (bg) return 'background-color:' + bg.trim() + ';';
    return '';
  }

  function pickerVariants(picker) {
    if (!picker) return [];
    var dataEl = picker.querySelector('script[type="application/json"]');
    if (!dataEl) return [];
    try {
      var parsed = JSON.parse(dataEl.textContent);
      return parsed.variants || [];
    } catch (e) { return []; }
  }

  function variantAvailable(picker, colorOption, colorValue, sizeOption, sizeValue) {
    var variants = pickerVariants(picker);
    if (!variants.length) return true; // can't tell -> don't block
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var opts = v.options || [];
      var matchSize = false, matchColor = colorValue == null;
      for (var k = 0; k < opts.length; k++) {
        if (sizeValue != null && eq(opts[k], sizeValue)) matchSize = true;
        if (colorValue != null && eq(opts[k], colorValue)) matchColor = true;
      }
      if (matchSize && matchColor) return !!v.available;
    }
    return true;
  }

  function scrollToTarget(sel, picker) {
    var target = (sel && document.querySelector(sel)) || picker;
    if (!target) return;
    var rect = target.getBoundingClientRect();
    var y = rect.top + (window.scrollY || window.pageYOffset) - 90;
    window.scrollTo({ top: y, left: 0, behavior: 'smooth' });
    target.classList.add('cdc-fit-flash');
    setTimeout(function () { target.classList.remove('cdc-fit-flash'); }, 1400);
  }

  /* ------------------------------------------------------------------ *
   * Popup (modal + sheet share markup; a class switches the styling).
   * ------------------------------------------------------------------ */
  function fill(str, size) { return String(str || '').replace(/\{size\}/g, size); }

  function buildPopup(cfg, ctx) {
    var mode = ctx.mode; // 'modal' | 'sheet'
    var copy = cfg.copy || {};
    var overlay = document.createElement('div');
    overlay.className = 'cdc-fit-pop cdc-fit-pop--' + mode;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', fill(copy.popupTitle || 'Your match', ctx.size));

    var colorsHtml = '';
    if (ctx.colorPick && ctx.colors.length) {
      var chips = ctx.colors.map(function (c) {
        var style = swatchStyleFor(ctx.picker, c);
        var selected = eq(c, ctx.selectedColor) ? ' is-selected' : '';
        return '<button type="button" class="cdc-fit-pop__swatch' + selected + '" data-color="' +
          c.replace(/"/g, '&quot;') + '" aria-pressed="' + (selected ? 'true' : 'false') + '">' +
          '<span class="cdc-fit-pop__swatch-dot" style="' + style + '"></span>' +
          '<span class="cdc-fit-pop__swatch-name">' + c + '</span></button>';
      }).join('');
      colorsHtml =
        '<div class="cdc-fit-pop__colors">' +
          '<p class="cdc-fit-pop__colors-label">' + (copy.colorLabel || 'Choose your colour') + '</p>' +
          '<div class="cdc-fit-pop__swatches">' + chips + '</div>' +
        '</div>';
    }

    overlay.innerHTML =
      '<div class="cdc-fit-pop__backdrop" data-close></div>' +
      '<div class="cdc-fit-pop__panel">' +
        '<button type="button" class="cdc-fit-pop__x" data-close aria-label="Close">&times;</button>' +
        (mode === 'sheet' ? '<span class="cdc-fit-pop__grip" aria-hidden="true"></span>' : '') +
        '<p class="cdc-fit-pop__eyebrow">' + (copy.popupEyebrow || 'Your match') + '</p>' +
        '<h3 class="cdc-fit-pop__title">' + fill(copy.popupTitle || 'Made for a Size <em>{size}</em>.', ctx.size) + '</h3>' +
        (copy.popupBody ? '<p class="cdc-fit-pop__body">' + fill(copy.popupBody, ctx.size) + '</p>' : '') +
        colorsHtml +
        '<p class="cdc-fit-pop__note" data-note hidden></p>' +
        '<button type="button" class="cdc-fit-pop__cta" data-confirm>' + (copy.confirm || 'Add my size') + '</button>' +
      '</div>';

    return overlay;
  }

  function openPopup(cfg, ctx) {
    var overlay = buildPopup(cfg, ctx);
    document.body.appendChild(overlay);
    document.documentElement.classList.add('cdc-fit-pop-open');

    var panel = overlay.querySelector('.cdc-fit-pop__panel');
    var noteEl = overlay.querySelector('[data-note]');
    var confirmBtn = overlay.querySelector('[data-confirm]');
    var chosenColor = ctx.colorPick ? ctx.selectedColor : null;
    var lastFocus = document.activeElement;

    function refreshNote() {
      var colorForCheck = ctx.colorPick ? chosenColor : ctx.selectedColor;
      var ok = variantAvailable(ctx.picker, cfg.colorOption, colorForCheck, cfg.sizeOption, ctx.sizeValue);
      if (!ok && (cfg.copy || {}).soldOut) {
        noteEl.textContent = fill(cfg.copy.soldOut, ctx.size);
        noteEl.hidden = false;
      } else {
        noteEl.hidden = true;
      }
    }
    refreshNote();

    // Reveal with a frame so the CSS transition runs.
    requestAnimationFrame(function () { overlay.classList.add('is-open'); });

    overlay.querySelectorAll('.cdc-fit-pop__swatch').forEach(function (btn) {
      btn.addEventListener('click', function () {
        chosenColor = btn.getAttribute('data-color');
        overlay.querySelectorAll('.cdc-fit-pop__swatch').forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        refreshNote();
      });
    });

    function close() {
      overlay.classList.remove('is-open');
      document.documentElement.classList.remove('cdc-fit-pop-open');
      overlay.addEventListener('transitionend', function handler() {
        overlay.removeEventListener('transitionend', handler);
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      });
      setTimeout(function () { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }, 400);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    overlay.querySelectorAll('[data-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    overlay.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    confirmBtn.addEventListener('click', function () {
      if (ctx.colorPick && chosenColor) {
        setOption(findSelector(ctx.picker, cfg.colorOption), chosenColor);
      }
      setOption(findSelector(ctx.picker, cfg.sizeOption), ctx.sizeValue);
      close();
      if (cfg.scroll) scrollToTarget(cfg.scrollTarget, ctx.picker);
      toast(cfg, ctx.size);
    });

    // Focus management.
    setTimeout(function () { confirmBtn.focus(); }, 60);
    panel.addEventListener('keydown', trapFocus(panel));
  }

  function trapFocus(panel) {
    return function (e) {
      if (e.key !== 'Tab') return;
      var focusable = panel.querySelectorAll('button, [href], input, select, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
  }

  /* ------------------------------------------------------------------ *
   * Toast (used by direct mode, and after any apply for confirmation).
   * ------------------------------------------------------------------ */
  function toast(cfg, size) {
    var msg = fill((cfg.copy || {}).toast || 'Size {size} selected', size);
    if (!msg) return;
    var el = document.createElement('div');
    el.className = 'cdc-fit-toast';
    el.setAttribute('role', 'status');
    el.textContent = msg;
    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-open'); });
    setTimeout(function () {
      el.classList.remove('is-open');
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 400);
    }, 2600);
  }

  /* ------------------------------------------------------------------ *
   * Per-root wiring.
   * ------------------------------------------------------------------ */
  function initRoot(root) {
    if (root.__cdcFitInit) return;
    root.__cdcFitInit = true;

    var cfg = {};
    var cfgEl = root.querySelector('script[type="application/json"][data-cdc-fit-config]');
    if (cfgEl) { try { cfg = JSON.parse(cfgEl.textContent) || {}; } catch (e) { cfg = {}; } }

    cfg.mode = root.getAttribute('data-cdc-fit-mode') || 'modal';
    cfg.pickerSelector = root.getAttribute('data-cdc-fit-picker') || 'variant-picker';
    cfg.sizeOption = root.getAttribute('data-cdc-fit-size-option') || 'Size';
    cfg.colorOption = root.getAttribute('data-cdc-fit-color-option') || 'Color';
    cfg.colorPick = root.getAttribute('data-cdc-fit-color-pick') !== 'false';
    cfg.scroll = root.getAttribute('data-cdc-fit-scroll') !== 'false';
    cfg.scrollTarget = root.getAttribute('data-cdc-fit-scroll-target') || cfg.pickerSelector;

    var chart = buildChart(cfg.chart);
    var overrides = parseOverrides(cfg.overrides);

    var bandSelect = root.querySelector('[data-cdc-fit-band]');
    var cupSelect = root.querySelector('[data-cdc-fit-cup]');
    var sizeOutput = root.querySelector('[data-cdc-fit-size]');
    var cta = root.querySelector('[data-cdc-fit-cta]');
    if (!bandSelect || !cupSelect || !cta) return;

    function currentSize() {
      return chart.compute(bandSelect.value, cupSelect.value, overrides);
    }

    function broadcast() {
      var size = currentSize();
      if (sizeOutput) sizeOutput.textContent = size;
      var state = { band: bandSelect.value, cup: cupSelect.value, size: size };
      try { sessionStorage.setItem('cdcFitState', JSON.stringify(state)); } catch (e) { /* no-op */ }
      document.dispatchEvent(new CustomEvent('cdc:fit:change', { detail: state, bubbles: true }));
    }

    bandSelect.addEventListener('change', broadcast);
    cupSelect.addEventListener('change', broadcast);

    cta.addEventListener('click', function () {
      broadcast();
      var size = currentSize();
      var picker = document.querySelector(cfg.pickerSelector);
      if (!picker) {
        // No picker on the page (e.g. editor preview). Fail quietly but visibly.
        toast(cfg, size);
        return;
      }

      var sizeSelector = findSelector(picker, cfg.sizeOption);
      var colorSelector = findSelector(picker, cfg.colorOption);
      var sizeValue = resolveSizeValue(sizeSelector, size);
      var colors = optionValues(colorSelector);
      var selectedColor = currentValue(colorSelector) || colors[0] || null;

      var ctx = {
        picker: picker,
        mode: cfg.mode,
        size: size,
        sizeValue: sizeValue,
        colors: colors,
        selectedColor: selectedColor,
        colorPick: cfg.colorPick && colors.length > 0
      };

      if (cfg.mode === 'direct') {
        // Set size (and keep the shopper's current colour), then confirm inline.
        if (sizeValue) setOption(sizeSelector, sizeValue);
        if (cfg.scroll) scrollToTarget(cfg.scrollTarget, picker);
        toast(cfg, size);
        return;
      }

      openPopup(cfg, ctx);
    });

    // Restore a previous selection within the session, then announce initial state.
    function hasOption(select, value) {
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value === value) return true;
      }
      return false;
    }
    var stored = null;
    try { stored = JSON.parse(sessionStorage.getItem('cdcFitState')); } catch (e) { /* no-op */ }
    if (stored && stored.band && hasOption(bandSelect, stored.band)) bandSelect.value = stored.band;
    if (stored && stored.cup && hasOption(cupSelect, stored.cup)) cupSelect.value = stored.cup;
    broadcast();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-cdc-fit]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  // Re-init after theme-editor section loads / dynamic section renders.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();